<template>
    <TransitionRoot as="template" :show="show">
        <Dialog as="div" class="fixed inset-0 overflow-hidden">
            <div class="absolute inset-0 overflow-hidden">
                <DialogOverlay class="absolute inset-0 bg-gray-500 opacity-75" @click="close()"/>

                <div class="fixed inset-y-0 pl-16 max-w-full right-0 flex">
                    <TransitionChild as="template" enter="transform transition ease-in-out duration-500 sm:duration-700"
                                     enter-from="translate-x-full" enter-to="translate-x-0"
                                     leave="transform transition ease-in-out duration-500 sm:duration-700"
                                     leave-from="translate-x-0" leave-to="translate-x-full">
                        <div class="w-screen">
                            <form class="h-full divide-y divide-gray-200 flex flex-col bg-white shadow-xl"
                                  @submit.prevent="submit">
                                <div class="flex-1 h-0 overflow-y-auto">
                                    <div class="py-6 px-4  bg-amaze-coral sm:px-6">
                                        <div class="flex items-center justify-between">
                                            <DialogTitle class="text-lg text-white font-bold">
                                                Manage Contacts for `{{ organisation.name}}`
                                            </DialogTitle>
                                            <div class="ml-3 h-7 flex items-center">
                                                <button type="button"
                                                        class="bg-amaze-coral rounded-md text-indigo-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                                                        @click="close()">
                                                    <span class="sr-only">Close panel</span>
                                                    <XIcon class="h-6 w-6" aria-hidden="true"/>
                                                </button>
                                            </div>
                                        </div>
                                        <div class="mt-1">
                                            <p class="text-sm text-white">
                                                Get started by filling in the information below to add contacts to the
                                                country.
                                            </p>
                                        </div>
                                    </div>
                                    <SlideOutErrors  :show="showErrors"/>
                                    <SlideoutFlashMessage ref="create_contacts_slideout_flash"/>
                                    <div class="flex-1 flex flex-col justify-between">
                                        <div class="px-4 divide-y divide-gray-200 sm:px-6">
                                            <div class="space-y-6 pt-6 pb-5">
                                                <h2 class="text-xl text-bold">Add New Contact</h2>
                                                <div class="grid grid-cols-4 gap-6">
                                                    <div>
                                                        <label for="contact-first-name"
                                                               class="block text-sm text-gray-900 required">
                                                            First Name
                                                        </label>
                                                        <div class="mt-1">
                                                            <input type="text" id="contact-first-name" v-model="form.first_name"
                                                                   class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                                   :class="{'border-red-500': form.errors.first_name}"
                                                            />
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <label for="contact-last-name"
                                                               class="block text-sm text-gray-900 required">
                                                            Last Name
                                                        </label>
                                                        <div class="mt-1">
                                                            <input type="text" id="contact-last-name" v-model="form.last_name"
                                                                   class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                                   :class="{'border-red-500': form.errors.last_name}"
                                                            />
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <label for="contact-position"
                                                               class="block text-sm text-gray-900 required">
                                                            Position
                                                        </label>
                                                        <div class="mt-1">
                                                            <input type="text" id="contact-position" v-model="form.position"
                                                                   class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                                   :class="{'border-red-500': form.errors.position}"
                                                            />
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <label for="contact-email"
                                                               class="block text-sm text-gray-900 required">
                                                            Email
                                                        </label>
                                                        <div class="mt-1">
                                                            <input type="text" id="contact-email" v-model="form.email"
                                                                   class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                                   :class="{'border-red-500': form.errors.email}"
                                                            />
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <label for="contact-telephone"
                                                               class="block text-sm text-gray-900 required">
                                                            Telephone
                                                        </label>
                                                        <div class="mt-1">
                                                            <input type="text" id="contact-telephone" v-model="form.telephone"
                                                                   class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                                   :class="{'border-red-500': form.errors.telephone}"
                                                            />
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <label for="contact-mobile"
                                                               class="block text-sm text-gray-900 required">
                                                            Mobile
                                                        </label>
                                                        <div class="mt-1">
                                                            <input type="text" id="contact-mobile" v-model="form.mobile"
                                                                   class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                                   :class="{'border-red-500': form.errors.mobile}"
                                                            />
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <label for="contact-type"
                                                               class="block text-sm text-gray-900 required">
                                                            Contact Type
                                                        </label>
                                                        <div class="mt-1">
                                                            <select v-model="form.contact_type" id="contact-type"
                                                                    :class="{'border-red-500': form.errors.contact_type}"
                                                                    class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm">
                                                                <option value selected>Select a contact type</option>
                                                                <option v-for="contact_type in contact_types" :value="contact_type"
                                                                        v-html="contact_type.charAt(0).toUpperCase() + contact_type.substring(1)"></option>
                                                            </select>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div class="flex justify-end">
                                                    <button :disabled="form.processing || !form.isDirty" type="submit"
                                                            class="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm rounded-md text-white bg-amaze-coral hover:bg-amaze-coral focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-500">
                                                        <span v-if="loading === true">Saving Contact..</span>
                                                        <span v-else>Add Contact</span>
                                                    </button>
                                                </div>

                                                <h2 class="text-xl text-bold">Contacts</h2>
                                                <div
                                                    class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg relative">

                                                    <table class="min-w-full divide-y divide-gray-200">
                                                        <thead class="bg-gray-50">
                                                        <tr>
                                                            <th scope="col"
                                                                class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                                First Name
                                                            </th>
                                                            <th scope="col"
                                                                class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                                Last Name
                                                            </th>
                                                            <th scope="col"
                                                                class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                                Email
                                                            </th>
                                                            <th scope="col"
                                                                class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                                Position
                                                            </th>
                                                            <th scope="col"
                                                                class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                                Contact Type
                                                            </th>
                                                            <th scope="col"
                                                                class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                                Telephone
                                                            </th>
                                                            <th scope="col"
                                                                class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                                Mobile
                                                            </th>
                                                            <th scope="col" class="relative px-6 py-3">
                                                                <span class="sr-only">Edit</span>
                                                            </th>
                                                        </tr>
                                                        </thead>
                                                        <tbody>
                                                        <tr v-if="organisation.contacts.length > 0" v-for="(contact, index) in contacts"
                                                            :class="{'bg-gray-100': index % 2}">
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                {{ contact.first_name }}
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                {{ contact.last_name }}
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                {{ contact.email }}
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                {{ contact.position }}
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                {{ contact.contact_type.charAt(0).toUpperCase() + contact.contact_type.substring(1) }}
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                {{ contact.telephone }}
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                {{ contact.mobile }}
                                                            </td>
                                                            <td class="px-6 w-1/5 py-4 whitespace-nowrap text-right text-sm">

                                                                <a @click="deleteContact(organisation, contact)"
                                                                   class="text-red-600 hover:text-red-900 float-right hover:cursor-pointer">
                                                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none"
                                                                         viewBox="0 0 24 24" class="w-4 h-4"
                                                                         stroke="currentColor">
                                                                        <path stroke-linecap="round"
                                                                              stroke-linejoin="round" stroke-width="2"
                                                                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                                                                    </svg>
                                                                </a>
                                                            </td>
                                                        </tr>
                                                        <tr v-else>
                                                            <td colspan="2"
                                                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900" :class="{'hidden':loading === true}">
                                                                There are no contacts added for this organisation.
                                                            </td>
                                                        </tr>
                                                        <tr v-if="loading === true && creating === true" :class="{'animate-pulse':loading === true}">
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                <span class="h-4 w-full block bg-slate-200 rounded">&nbsp;</span>
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                <span class="h-4 w-full block bg-slate-200 rounded">&nbsp;</span>
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                <span class="h-4 w-full block bg-slate-200 rounded">&nbsp;</span>
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                <span class="h-4 w-full block bg-slate-200 rounded">&nbsp;</span>
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                <span class="h-4 w-full block bg-slate-200 rounded">&nbsp;</span>
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                <span class="h-4 w-full block bg-slate-200 rounded">&nbsp;</span>
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                <span class="h-4 w-full block bg-slate-200 rounded">&nbsp;</span>
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                <span class="h-4 w-full block bg-slate-200 rounded">&nbsp;</span>
                                                            </td>
                                                        </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>


                                <div class="flex-shrink-0 px-4 py-4 flex justify-end">
                                    <button type="button"
                                            class="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                            @click="close()">
                                        Close
                                    </button>
                                </div>
                            </form>
                        </div>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>
<script>
import {ref, reactive, computed} from 'vue'
import {Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot} from '@headlessui/vue'
import {XIcon} from '@heroicons/vue/outline'
import {LinkIcon, PlusSmIcon, QuestionMarkCircleIcon} from '@heroicons/vue/solid'
import {useForm, usePage} from '@inertiajs/vue3'


import SlideOutErrors from "@/Components/SlideOutErrors.vue";
import SlideoutFlashMessage from "@/Components/SlideoutFlashMessage";


export default {
    components: {
        SlideOutErrors,
        Dialog,
        DialogOverlay,
        DialogTitle,
        TransitionChild,
        TransitionRoot,
        LinkIcon,
        PlusSmIcon,
        QuestionMarkCircleIcon,
        XIcon,
        SlideoutFlashMessage
    },
    setup(prop, context) {

        const organisation = ref(null);

        const contacts = ref(null);

        const contact_types = computed(() => usePage().props.contact_types);

        const loading = ref(null);
        const creating = ref(null);

        const show = ref(false)

        const showErrors = ref(false)

        const form = useForm({
            id: null,
            first_name: null,
            last_name: null,
            position: null,
            email: null,
            telephone: null,
            mobile: null,
            contact_type: "",
        })

        function submit() {
            this.loading = true;
            this.creating = true;
            this.form.post(route('frontend.organisations.organisation.contacts.create', this.organisation.id), {
                onSuccess: (page) => {
                    this.form.reset();
                    // Make a request for a contacts to be fetched.
                    axios.get(route('frontend.organisations.organisation.contacts.list', this.organisation.id))
                        .then(response => {
                            this.contacts = response.data;
                            this.organisation.contacts = response.data;
                        }).finally(() => {
                        this.loading = false
                        this.creating = false;
                    });
                },
                onError: (page) => {
                    this.loading = false;
                    this.creating = false;
                    this.showErrors = true;
                }
            });
        }

        function open() {
            this.form.clearErrors();
            this.show = true;
        }


        return {
            show,
            form,
            submit,
            organisation,
            contacts,
            loading,
            creating,
            open,
            contact_types,
            showErrors
        }
    },
    methods: {
        deleteContact(organisation, contact) {

            this.$swal.fire({
                title: 'Remove Contact',
                html: "You are removing a contact. <br/>Are you sure you'd like to do this?",
                showCloseButton: true,
                showCancelButton: true,
                confirmButtonText: 'Yes',
                cancelButtonText: 'No',
                stopKeydownPropagation: true,
                keydownListenerCapture: true
            })
                .then((result) => {
                    if (result.isConfirmed) {
                        // Trigger the loader
                        this.loading = true;
                        // Delete the contact
                        Inertia.delete(route('frontend.organisations.organisation.contacts.delete', [organisation, contact]), {
                            preserveScroll: true,
                            onSuccess: (page) => {
                                // Make a request for a contacts to be fetched.
                                axios.get(route('frontend.organisations.organisation.contacts.list', this.organisation.id))
                                    .then(response => {
                                        this.contacts = response.data;
                                        this.organisation.contacts = response.data;
                                    }).finally(() => {
                                    this.loading = false
                                });
                            },
                            onError: (page) => {
                                this.loading = false
                                this.showErrors = true
                            }
                        });
                    }
                });
        },
        close() {
            this.show = false;
            this.form.reset();
            this.form.clearErrors();
            this.$refs.create_contacts_slideout_flash.show = false;
            this.$page.props.slideout_flash.success = null;
            this.$page.props.slideout_flash.error = null;
        }
    }
}
</script>
